import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux'; 
import { getDelegatesList } from '../../redux/slices/delegates/delegatesThunk';
import { 
    Select, Table, Typography, Tag, Space, Card, Row, Col, 
    DatePicker, Button, Divider, message, Tooltip, Modal, Input, Popconfirm, Checkbox,
    Spin, List, Empty,
    Collapse, Badge
} from 'antd';
import { 
    ReloadOutlined, ScheduleOutlined, DownloadOutlined, 
    BarChartOutlined, EditOutlined, DeleteOutlined,
    EnvironmentOutlined, MedicineBoxOutlined, StarFilled,
    CheckCircleFilled, AuditOutlined, UserOutlined, RocketOutlined,
    CrownOutlined,
    WarningOutlined, CheckCircleOutlined 
} from '@ant-design/icons';
import { getAllDelegatesList } from '../../services/delegateService';
import { getWeeklyMissedVisits } from '../../services/analyticsService';
import axiosInstance from '../../services/axiosServicesVisit';
import DoctorHistoryModal from '../../components/DoctorHistoryModal';
import dayjs from 'dayjs';
import Papa from 'papaparse';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const WeeklyMissedVisitsPage = () => {
     const dispatch = useDispatch();
    const reduxDelegates = useSelector((state) => state.delegates?.delegatesList?.data?.data || []);
    const [delegates, setDelegates] = useState([]);
    const [selectedDelegateId, setSelectedDelegateId] = useState(null);
    const [dateRange, setDateRange] = useState([dayjs('2026-01-01'), dayjs('2026-12-31')]);
    const [visitStatusFilter, setVisitStatusFilter] = useState('all');
    const [selectedCycleIds, setSelectedCycleIds] = useState([]); 
    const [selectedWeek, setSelectedWeek] = useState('all'); 
    const [loading, setLoading] = useState(false);
    const [rawApiData, setRawApiData] = useState([]); 
    const [analysisData, setAnalysisData] = useState({ tableData: [], cycleOptions: [] });

    // États édition justification
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editingComment, setEditingComment] = useState({ id: null, doctor: '', text: '', week: '' });
    const [actionLoading, setActionLoading] = useState(false);

    // Filtre médecin
    const [selectedPhysicianId, setSelectedPhysicianId] = useState(null);

    // États modale Parcours de visite du Médecin
    const [doctorModalVisible, setDoctorModalVisible] = useState(false);
    const [selectedDoctorId, setSelectedDoctorId] = useState(null);

    const handleOpenDoctorHistory = (personId) => {
        setSelectedDoctorId(personId);
        setDoctorModalVisible(true);
    };

    const allKnownDelegates = useMemo(() => {
        const map = new Map();
        [...(reduxDelegates || []), ...(delegates || [])].forEach(d => {
            if (d && d.id) {
                const prev = map.get(d.id);
                map.set(d.id, { ...prev, ...d, category: d.category || prev?.category });
            }
        });
        return Array.from(map.values());
    }, [reduxDelegates, delegates]);

    const getCategoryTagColor = (cat) => {
        if (!cat) return 'default';
        const c = String(cat).toUpperCase();
        if (c === 'AKTIV') return 'blue';
        if (c === 'SANOSAN') return 'green';
        if (c === 'MATERNITE') return 'purple';
        return 'cyan';
    };

    useEffect(() => {
        getAllDelegatesList().then(res => setDelegates(res.data || []));
        dispatch(getDelegatesList({ page: 1, itemsPerPage: 1000 })); 
    }, [dispatch]);

    const fetchAnalysis = () => {
        if (!selectedDelegateId) return;
        setLoading(true);
        const start = dateRange ? dateRange[0].format('YYYY-MM-DD') : undefined;
        const end = dateRange ? dateRange[1].format('YYYY-MM-DD') : undefined;

        getWeeklyMissedVisits(selectedDelegateId, start, end)
            .then(response => {
                setRawApiData(response.data || []); 
                setAnalysisData(formatDataForTable(response.data || []));
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchAnalysis(); }, [selectedDelegateId, dateRange]);

    const toggleCycleSelection = (cycleId) => {
        setSelectedCycleIds(prev => 
            prev.includes(cycleId) 
                ? prev.filter(id => id !== cycleId) 
                : [...prev, cycleId]
        );
    };

    

    const handleOpenEdit = (doctorName, reasonObj, weekNum) => {
        setEditingComment({ id: reasonObj.id, doctor: doctorName, text: reasonObj.comment, week: weekNum });
        setIsEditModalVisible(true);
    };

    const handleUpdateComment = async () => {
        if (!editingComment.text) return message.warning("Le texte ne peut pas être vide.");
        setActionLoading(true);
        try {
            await axiosInstance.patch(`/v1/analytics/missed-visit-justification/${editingComment.id}`, { comment: editingComment.text });
            message.success("Justification mise à jour.");
            setIsEditModalVisible(false);
            fetchAnalysis(); 
        } catch (e) { message.error("Erreur lors de la mise à jour"); }
        finally { setActionLoading(false); }
    };

    const handleDeleteComment = async (id) => {
        try {
            await axiosInstance.delete(`/v1/analytics/missed-visit-justification/${id}`);
            message.success("Justification supprimée.");
            fetchAnalysis();
        } catch (e) { message.error("Erreur lors de la suppression"); }
    };

    const formatDataForTable = (apiData) => {
        const physicianMap = new Map();
        const cycleOptions = apiData.map(c => ({ value: c.cycleId, label: c.cycleName }));

        apiData.forEach(cycle => {
            cycle.planning.forEach(plan => {
                if (!physicianMap.has(plan.person.id)) {
                    physicianMap.set(plan.person.id, { 
                        key: plan.person.id, 
                        physicianName: `${plan.person.firstname} ${plan.person.lastname}`,
                        specialty: plan.person.specialtyName,
                        region: plan.person.regionName,
                        isPartner: plan.person.isPartner,
                        partnerNotes: plan.person.partnerNotes
                    });
                }
                const row = physicianMap.get(plan.person.id);
                row[`cycle_${cycle.cycleId}`] = { 
                    cycleName: cycle.cycleName, 
                    missed: plan.missedInWeeks || [], 
                    visited: plan.visitedInWeeks || [], 
                    recovered: plan.recoveredInfo || [], 
                    justificationsByWeek: plan.justificationsByWeek 
                };
            });
        });

        return { tableData: Array.from(physicianMap.values()), cycleOptions };
    };

    // Statistiques visites par cycle (visités / total prévus)
    const cycleStatsMap = useMemo(() => {
        const stats = {};
        const weekNum = selectedWeek !== 'all' ? parseInt(selectedWeek, 10) : null;

        rawApiData.forEach(cycle => {
            let totalVisited = 0;
            let totalPlanned = 0;

            (cycle.planning || []).forEach(plan => {
                if (weekNum) {
                    const isVisited = plan.visitedInWeeks?.includes(weekNum) || plan.recoveredInfo?.some(r => r.missed === weekNum);
                    const isMissed = plan.missedInWeeks?.includes(weekNum);
                    if (isVisited) {
                        totalVisited++;
                        totalPlanned++;
                    } else if (isMissed) {
                        totalPlanned++;
                    }
                } else {
                    const vCount = (plan.visitedInWeeks?.length || 0) + (plan.recoveredInfo?.length || 0);
                    const mCount = plan.missedInWeeks?.length || 0;
                    totalVisited += vCount;
                    totalPlanned += (vCount + mCount);
                }
            });

            const percent = totalPlanned > 0 ? Math.round((totalVisited / totalPlanned) * 100) : 0;
            stats[cycle.cycleId] = { totalVisited, totalPlanned, percent };
        });

        return stats;
    }, [rawApiData, selectedWeek]);

    // Colonnes dynamiques avec compteurs dans le titre
    const dynamicCycleColumns = useMemo(() => {
        const weekNum = selectedWeek !== 'all' ? parseInt(selectedWeek, 10) : null;

        return rawApiData.map(cycle => {
            const stats = cycleStatsMap[cycle.cycleId] || { totalVisited: 0, totalPlanned: 0, percent: 0 };
            const isCompleted = stats.totalPlanned > 0 && stats.totalVisited === stats.totalPlanned;

            return {
                title: (
                    <div style={{ textAlign: 'center' }}>
                        <Text strong style={{ color: '#003a8c', display: 'block', fontSize: '13px' }}>
                            {cycle.cycleName}
                        </Text>
                        <div style={{ marginTop: '4px' }}>
                            <Tag 
                                color={isCompleted ? 'success' : stats.totalVisited > 0 ? 'blue' : 'default'} 
                                style={{ 
                                    fontWeight: 'bold', 
                                    margin: 0, 
                                    padding: '1px 8px', 
                                    borderRadius: '10px',
                                    fontSize: '11px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                            >
                                {stats.totalVisited} / {stats.totalPlanned} ({stats.percent}%)
                            </Tag>
                        </div>
                    </div>
                ),
                dataIndex: `cycle_${cycle.cycleId}`,
                key: `cycle_${cycle.cycleId}`,
                width: 150,
                align: 'center',
                render: (info, record) => {
                    if (!info) return <Text type="secondary">-</Text>;
                    
                    return (
                        <Space direction="vertical" size={3} style={{ width: '100%' }}>
                            {info.visited?.filter(w => !weekNum || w === weekNum).map(w => (
                                <Tag color="green" key={`v_${w}`} style={{ width: '100%', textAlign: 'center', margin: 0 }}>
                                    S{w} Visité
                                </Tag>
                            ))}

                            {info.recovered?.filter(r => !weekNum || r.missed === weekNum).map((r, idx) => (
                                <Tooltip key={`rec_${idx}`} title={`Prévu en S${r.missed}, mais visité en S${r.recoveredBy}`}>
                                    <Tag color="cyan" style={{ width: '100%', textAlign: 'center', margin: 0, fontWeight: 'bold' }}>
                                        S{r.missed} Rattrapé (en S{r.recoveredBy})
                                    </Tag>
                                </Tooltip>
                            ))}

                            {info.missed?.filter(w => !weekNum || w === weekNum).map(w => (
                                <div key={`m_${w}`} style={{ width: '100%' }}>
                                    <Tag color="error" style={{ width: '100%', textAlign: 'center', margin: 0 }}>
                                        S{w} Manqué
                                    </Tag>
                                    {info.justificationsByWeek && info.justificationsByWeek[w] && (
                                        <div style={{ marginTop: 4, padding: '4px', background: '#fff1f0', border: '1px dashed #ffa39e', borderRadius: '4px' }}>
                                            <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#cf1322', marginBottom: 4, textAlign: 'center' }}>
                                                "{info.justificationsByWeek[w].comment}"
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                                                <EditOutlined style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => handleOpenEdit(record.physicianName, info.justificationsByWeek[w], w)} />
                                                <Popconfirm title="Supprimer cette raison ?" onConfirm={() => handleDeleteComment(info.justificationsByWeek[w].id)}>
                                                    <DeleteOutlined style={{ color: '#ff4d4f', cursor: 'pointer' }} />
                                                </Popconfirm>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </Space>
                    );
                }
            };
        });
    }, [rawApiData, selectedWeek, cycleStatsMap]);

    const physicianOptions = useMemo(() => {
        return analysisData.tableData.map(row => ({
            value: row.key,
            label: `${row.isPartner ? '⭐ ' : ''}${row.physicianName}`,
            rawName: row.physicianName
        })).sort((a, b) => a.rawName.localeCompare(b.rawName));
    }, [analysisData.tableData]);

    const finalColumns = useMemo(() => {
        const base = [{ 
            title: 'Médecin', dataIndex: 'physicianName', key: 'physicianName', fixed: 'left', width: 230, 
            render: (text, record) => (
                <div>
                    <Space>
                        {record.isPartner && <StarFilled style={{ color: '#fadb14' }} />}
                        <a 
                            onClick={() => handleOpenDoctorHistory(record.key)}
                            style={{ 
                                fontWeight: 'bold', 
                                color: record.isPartner ? '#856404' : '#1890ff', 
                                fontSize: '13px',
                                textDecoration: 'underline'
                            }}
                        >
                            {text}
                        </a>
                    </Space>
                    <div style={{ marginTop: '2px', display: 'flex', flexDirection: 'column' }}>
                        <Text type="secondary" style={{ fontSize: '10px' }}><MedicineBoxOutlined /> {record.specialty}</Text>
                        <Text type="secondary" style={{ fontSize: '10px' }}><EnvironmentOutlined /> {record.region}</Text>
                    </div>
                </div>
            )
        }];
        let dynamic = dynamicCycleColumns;
        if (selectedCycleIds.length > 0) {
            const selectedIdsNumbers = selectedCycleIds.map(Number);
            dynamic = dynamic.filter(col => selectedIdsNumbers.includes(Number(col.key.replace('cycle_', ''))));
        }
        return [...base, ...dynamic];
    }, [dynamicCycleColumns, selectedCycleIds]);

    const filteredTableData = useMemo(() => {
        return analysisData.tableData.filter(row => {
            if (selectedPhysicianId && row.key !== selectedPhysicianId) {
                return false;
            }

            const cycleKeys = Object.keys(row).filter(k => k.startsWith('cycle_'));
            const activeKeys = selectedCycleIds.length > 0 ? cycleKeys.filter(k => selectedCycleIds.includes(parseInt(k.replace('cycle_', '')))) : cycleKeys;
            if (activeKeys.length === 0 && selectedCycleIds.length > 0) return false;
            
            return activeKeys.some(k => {
                const cell = row[k]; if (!cell) return false;
                const weekNum = selectedWeek !== 'all' ? parseInt(selectedWeek) : null;
                if (weekNum && !cell.visited.includes(weekNum) && !cell.missed.includes(weekNum)) return false;
                if (visitStatusFilter === 'all') return true;
                if (visitStatusFilter === 'visited') return weekNum ? cell.visited.includes(weekNum) : cell.visited.length > 0;
                if (visitStatusFilter === 'nonVisited') return weekNum ? cell.missed.includes(weekNum) : cell.missed.length > 0;
                return true;
            });
        });
    }, [analysisData.tableData, visitStatusFilter, selectedWeek, selectedCycleIds, selectedPhysicianId]);

    // Export CSV
    const handleExportCsv = () => {
        if (!filteredTableData || filteredTableData.length === 0) {
            return message.warning("Aucune donnée à exporter.");
        }

        const visibleCycles = rawApiData.filter(cycle => {
            if (selectedCycleIds.length === 0) return true;
            return selectedCycleIds.includes(cycle.cycleId);
        });

        const weekNum = selectedWeek !== 'all' ? parseInt(selectedWeek, 10) : null;

        const exportRows = filteredTableData.map(row => {
            const item = {
                'Médecin': row.physicianName,
                'Partenaire': row.isPartner ? 'Oui' : 'Non',
                'Spécialité': row.specialty || '',
                'Région': row.region || '',
            };

            visibleCycles.forEach(cycle => {
                const cell = row[`cycle_${cycle.cycleId}`];
                const stats = cycleStatsMap[cycle.cycleId];
                const cycleHeaderName = `${cycle.cycleName} (${stats ? `${stats.totalVisited}/${stats.totalPlanned}` : ''})`;

                if (!cell) {
                    item[cycleHeaderName] = '-';
                    return;
                }

                const details = [];

                cell.visited?.filter(w => !weekNum || w === weekNum).forEach(w => {
                    details.push(`S${w} Visité`);
                });

                cell.recovered?.filter(r => !weekNum || r.missed === weekNum).forEach(r => {
                    details.push(`S${r.missed} Rattrapé (en S${r.recoveredBy})`);
                });

                cell.missed?.filter(w => !weekNum || w === weekNum).forEach(w => {
                    const justif = cell.justificationsByWeek && cell.justificationsByWeek[w]?.comment 
                        ? ` [Raison: ${cell.justificationsByWeek[w].comment}]` 
                        : '';
                    details.push(`S${w} Manqué${justif}`);
                });

                item[cycleHeaderName] = details.length > 0 ? details.join(' | ') : '-';
            });

            return item;
        });

        const csvString = Papa.unparse(exportRows, { delimiter: ';' });
        const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        const selectedDelegate = delegates.find(d => d.id === selectedDelegateId);
        const delegateName = selectedDelegate ? `${selectedDelegate.firstname}_${selectedDelegate.lastname}` : 'delegue';
        const dateStr = dayjs().format('YYYY-MM-DD');

        link.setAttribute('href', url);
        link.setAttribute('download', `analyse_visites_${delegateName}_${dateStr}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        message.success("Export CSV téléchargé avec succès.");
    };

    return (
        <div style={{ padding: '20px' }}>
            <Card title={<Space><BarChartOutlined /> <Text strong style={{fontSize: '20px'}}>Analyse Hebdomadaire des Visites</Text></Space>} bordered={false}>
                <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 20 }}>
                    <Col>
                        <Select 
                            showSearch 
                            placeholder="Délégué" 
                            style={{ width: 180 }} 
                            onChange={v => { setSelectedDelegateId(v); setSelectedCycleIds([]); setSelectedPhysicianId(null); }} 
                            optionFilterProp="label"
                            options={delegates.map(d => ({ value: d.id, label: `${d.firstname} ${d.lastname}` }))} 
                        />
                    </Col>
                    <Col><RangePicker value={dateRange} onChange={setDateRange} format="DD/MM/YYYY" style={{ width: 230 }} /></Col>
                    <Col>
                        <Select 
                            showSearch 
                            allowClear
                            placeholder="Filtrer Médecin" 
                            style={{ width: 210 }} 
                            value={selectedPhysicianId} 
                            onChange={setSelectedPhysicianId}
                            disabled={!selectedDelegateId || analysisData.tableData.length === 0}
                            optionFilterProp="label"
                            filterOption={(input, option) => 
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={physicianOptions}
                        />
                    </Col>
                    <Col>
                        <Select 
                            mode="multiple" 
                            placeholder="Filtrer Cycles" 
                            style={{ width: 220 }} 
                            value={selectedCycleIds} 
                            onChange={setSelectedCycleIds} 
                            maxTagCount="responsive"
                            optionLabelProp="label"
                        >
                            {analysisData.cycleOptions.map(opt => (
                                <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                                    <Checkbox checked={selectedCycleIds.includes(opt.value)} /> {opt.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col><Select value={selectedWeek} onChange={setSelectedWeek} style={{ width: 140 }} options={[{value:'all', label:'Toutes Semaines'},{value:'1', label:'Semaine 1'},{value:'2', label:'Semaine 2'},{value:'3', label:'Semaine 3'},{value:'4', label:'Semaine 4'}]} /></Col>
                    <Col><Select value={visitStatusFilter} onChange={setVisitStatusFilter} style={{ width: 130 }} options={[{value:'all', label:'Tous Statuts'},{value:'visited', label:'Visités'},{value:'nonVisited', label:'Manqués'}]} /></Col>
                    
                    <Col>
                        <Space>
                            <Button type="primary" icon={<ReloadOutlined />} onClick={fetchAnalysis}>Actualiser</Button>
                            <Button 
                                icon={<DownloadOutlined />} 
                                onClick={handleExportCsv} 
                                disabled={!selectedDelegateId || filteredTableData.length === 0}
                            >
                                Exporter CSV
                            </Button>
                        </Space>
                    </Col>
                </Row>

                {/* ZONE DES BADGES (CALENDRIER DES CYCLES) */}
                {selectedDelegateId && rawApiData.length > 0 && (
                    <div style={{ padding: '8px 12px', background: '#f0f5ff', borderRadius: '6px', border: '1px solid #adc6ff', marginBottom: 15 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <Text strong style={{ color: '#002766', fontSize: '13px', opacity: 0.7 }}><ScheduleOutlined /> CLIQUEZ SUR UN CYCLE POUR FILTRER :</Text>
                            <Button size="small" type="link" onClick={() => setSelectedCycleIds([])}>Réinitialiser filtres</Button>
                        </div>
                        <Row gutter={[6, 6]}>
                            {[...rawApiData].sort((a, b) => a.cycleId - b.cycleId).map(cycle => {
                                const isSelected = selectedCycleIds.length === 0 || selectedCycleIds.includes(cycle.cycleId);
                                const stats = cycleStatsMap[cycle.cycleId] || { totalVisited: 0, totalPlanned: 0 };

                                return (
                                    <Col key={cycle.cycleId} xs={24} sm={12} lg={8}>
                                        <Card 
                                            hoverable
                                            size="small" 
                                            onClick={() => toggleCycleSelection(cycle.cycleId)}
                                            style={{ 
                                                borderRadius: '4px', 
                                                border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9', 
                                                background: isSelected ? '#fff' : '#fafafa',
                                                opacity: isSelected ? 1 : 0.6,
                                                position: 'relative'
                                            }} 
                                            bodyStyle={{ padding: '4px 8px' }}
                                        >
                                            {selectedCycleIds.includes(cycle.cycleId) && (
                                                <CheckCircleFilled style={{ position: 'absolute', top: -5, right: -5, color: '#1890ff', fontSize: '16px', background: '#fff', borderRadius: '50%' }} />
                                            )}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                                <Text strong style={{ fontSize: '11px', color: isSelected ? '#1890ff' : '#8c8c8c' }}>{cycle.cycleName.toUpperCase()}</Text>
                                                <Tag color="blue" style={{ fontSize: '10px', padding: '0 4px', margin: 0, lineHeight: '18px' }}>
                                                    {stats.totalVisited}/{stats.totalPlanned}
                                                </Tag>
                                            </div>
                                            <div style={{ display: 'flex', gap: '3px' }}>
                                                {cycle.weeks?.map(w => (
                                                    <div key={w.num} style={{ flex: 1, background: isSelected ? '#f5f5f5' : '#f0f0f0', borderRadius: '3px', padding: '2px 0', textAlign: 'center', border: '1px solid #e8e8e8' }}>
                                                        <div style={{ fontSize: '10px', color: '#8c8c8c' }}>S{w.num}</div>
                                                        <div style={{ fontSize: '11px', fontWeight: 'bold' }}>{dayjs(w.start).format('DD/MM')}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>
                )}

                <Table 
                    loading={loading} 
                    dataSource={filteredTableData} 
                    columns={finalColumns} 
                    bordered 
                    size="small" 
                    rowClassName={(record) => record.isPartner ? 'row-partner-gold' : ''} 
                    scroll={{ x: 'max-content', y: 550 }} 
                    pagination={{ pageSize: 20 }} 
                />

                {/* MODALE ÉDITION JUSTIFICATION */}
                <Modal 
                    title="Modifier la justification" 
                    open={isEditModalVisible} 
                    onCancel={() => setIsEditModalVisible(false)} 
                    onOk={handleUpdateComment} 
                    confirmLoading={actionLoading} 
                    okText="Mettre à jour" 
                    cancelText="Annuler"
                >
                    <div style={{ padding: '10px 0' }}>
                        <p><Text strong>Médecin :</Text> {editingComment.doctor}</p>
                        <Divider style={{ margin: '12px 0' }} />
                        <Input.TextArea rows={4} value={editingComment.text} onChange={(e) => setEditingComment({...editingComment, text: e.target.value})} />
                    </div>
                </Modal>

                {/* ========================================================================= */}
                {/* ⚡ MODALE PARCOURS DE VISITE DU MÉDECIN AVEC DISTINCTION SUPERVISEUR ⚡ */}
                {/* ========================================================================= */}
                <DoctorHistoryModal
                    visible={doctorModalVisible}
                    onClose={() => setDoctorModalVisible(false)}
                    personId={selectedDoctorId}
                />
            </Card>

            <style>{`
                .row-partner-gold > td { background-color: #fffbe6 !important; }
                .row-partner-gold > td.ant-table-cell-fix-left { background-color: #fffbe6 !important; }
                .row-partner-gold:hover > td { background-color: #fff1b8 !important; }
                .row-partner-gold a { color: #856404 !important; }
            `}</style>
        </div>
    );
};

export default WeeklyMissedVisitsPage;